/**
 * Hook für Feature-Zugangs-Prüfung
 *
 * Prüft ob der aktuelle User Zugang zu einem Feature hat,
 * basierend auf Feature-Status (beta/live) und User-Status (Beta-Tester, Abo).
 *
 * Verwendet Batch-Requests: Mehrere gleichzeitige Feature-Checks werden
 * automatisch zu einem einzigen API-Call zusammengefasst.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api/apiClient';

// ============================================
// Types
// ============================================

export type FeatureStatus = 'development' | 'beta' | 'live';

export interface FeatureAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
  error: string | null;
  featureStatus: FeatureStatus | null;
  isBetaFeature: boolean;
  reason?: string;
  refetch: () => Promise<void>;
}

interface FeatureAccessData {
  hasAccess: boolean;
  featureStatus: FeatureStatus;
  isBetaFeature: boolean;
  reason?: string;
}

interface FeatureAccessResponse {
  success: boolean;
  data: FeatureAccessData;
}

interface BatchFeatureAccessResponse {
  success: boolean;
  data: Record<string, FeatureAccessData>;
}

// ============================================
// Cache für Feature-Zugriffe
// ============================================

const featureAccessCache = new Map<string, {
  data: FeatureAccessData;
  timestamp: number;
}>();

// Cache-Lifetime: 5 Minuten
const CACHE_TTL = 5 * 60 * 1000;

function getCachedAccess(featureKey: string, userId: string | null, userRole?: string): FeatureAccessData | null {
  const cacheKey = `${featureKey}:${userId || 'anon'}:${userRole || 'none'}`;
  const cached = featureAccessCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }

  return null;
}

function setCachedAccess(featureKey: string, userId: string | null, data: FeatureAccessData, userRole?: string): void {
  const cacheKey = `${featureKey}:${userId || 'anon'}:${userRole || 'none'}`;
  featureAccessCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
  });
}

// Cache invalidieren (z.B. nach Login/Logout)
export function invalidateFeatureAccessCache(): void {
  featureAccessCache.clear();
}

// ============================================
// Batch-Request-System
// ============================================

// Sammelt Feature-Keys und löst sie gebatcht auf
let pendingBatchKeys: Set<string> = new Set();
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let batchPromise: Promise<Record<string, FeatureAccessData>> | null = null;
let batchResolvers: Array<{
  resolve: (value: Record<string, FeatureAccessData>) => void;
  reject: (reason: any) => void;
}> = [];

async function flushBatch(): Promise<void> {
  const keys = Array.from(pendingBatchKeys);
  const resolvers = batchResolvers;

  // Reset State VOR dem Request, damit neue Aufrufe schon einen neuen Batch starten
  pendingBatchKeys = new Set();
  batchTimer = null;
  batchPromise = null;
  batchResolvers = [];

  try {
    let results: Record<string, FeatureAccessData>;

    if (keys.length === 1) {
      const response = await apiClient.get<FeatureAccessResponse>(
        `/features/${keys[0]}/access`
      );
      const key = keys[0] as string;
      results = { [key]: response.data.data };
    } else {
      const response = await apiClient.post<BatchFeatureAccessResponse>(
        '/features/batch-access',
        { featureKeys: keys }
      );
      results = response.data.data;
    }

    resolvers.forEach((r) => r.resolve(results));
  } catch (error) {
    resolvers.forEach((r) => r.reject(error));
  }
}

function requestBatchAccess(featureKey: string): Promise<Record<string, FeatureAccessData>> {
  pendingBatchKeys.add(featureKey);

  // Timer immer (neu) starten, damit weitere synchrone Subscriber noch in den Batch fallen.
  if (batchTimer) {
    clearTimeout(batchTimer);
  }
  batchTimer = setTimeout(() => {
    void flushBatch();
  }, 10);

  // Existiert schon eine Promise, geben wir DIESELBE zurück. Alle Subscriber
  // teilen sich dieselbe Promise und denselben Resolver, der durch flushBatch
  // mit dem kompletten Batch-Ergebnis aufgelöst wird.
  if (batchPromise) {
    return batchPromise;
  }

  batchPromise = new Promise((resolve, reject) => {
    batchResolvers.push({ resolve, reject });
  });

  return batchPromise;
}

// ============================================
// Hook
// ============================================

/**
 * Hook zum Prüfen des Feature-Zugangs
 *
 * @param featureKey - Der eindeutige Key des Features (z.B. 'blog_analysis_tab')
 * @returns FeatureAccessResult mit Zugangs-Info, Lade-Status und Fehler
 */
export function useFeatureAccess(featureKey: string): FeatureAccessResult {
  const { user, isAuthenticated, hasFcmsAccess, hasBitcoinAccess } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessData, setAccessData] = useState<FeatureAccessData | null>(null);

  // Nur auf stabile Primitive reagieren, NICHT auf das ganze user-Objekt.
  // Periodische Profil-Refreshes (60s-Timer, Fokus-Handler in AuthContext)
  // erzeugen eine neue user-Referenz, obwohl sich id/role meist gar nicht
  // ändern -- ohne diese Trennung würde jeder Background-Refresh hier einen
  // neuen Fetch samt isLoading=true auslösen und z.B. FeatureGate die
  // Kinder unmounten (siehe Bugfix Plan-Trade-Zeichenabbruch, 2026-08-15).
  const userId = user?.id ?? null;
  const userRole = user?.role ?? null;

  const fetchAccess = useCallback(async () => {
    if (!featureKey) {
      setIsLoading(false);
      setError('Kein Feature-Key angegeben');
      return;
    }

    // Nicht eingeloggte User haben keinen Zugang zu gated Features
    if (!isAuthenticated || !user) {
      setAccessData({
        hasAccess: false,
        featureStatus: 'live',
        isBetaFeature: false,
        reason: 'Anmeldung erforderlich',
      });
      setIsLoading(false);
      return;
    }

    // Cache pruefen
    const cached = getCachedAccess(featureKey, user.id, user.role);
    if (cached) {
      setAccessData(cached);
      setIsLoading(false);
      return;
    }

    // isLoading nur setzen, wenn wir noch keine Daten haben. Ein Refetch
    // nach Cache-Invalidierung (z.B. durch periodischen Auth-Refresh) soll
    // im Hintergrund laufen, ohne Consumer wie FeatureGate kurzzeitig in
    // den Ladezustand zurückzuwerfen und damit gemountete Kinder zu killen.
    if (!accessData) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Batch-Request nutzen (sammelt mehrere gleichzeitige Aufrufe)
      const results = await requestBatchAccess(featureKey);
      const data = results[featureKey];

      if (data) {
        setAccessData(data);
        setCachedAccess(featureKey, user.id, data, user.role);
      } else {
        throw new Error('Feature-Zugang konnte nicht geprüft werden');
      }
    } catch (err: any) {
      // 429-Fehler still behandeln (kein console.error, kein Toast)
      if (err.response?.status === 429) {
        setAccessData({
          hasAccess: false,
          featureStatus: 'live',
          isBetaFeature: false,
          reason: 'Bitte später erneut versuchen',
        });
      } else if (err.response?.status === 404) {
        const fallbackData: FeatureAccessData = {
          hasAccess: false,
          featureStatus: 'development',
          isBetaFeature: false,
          reason: 'Feature nicht gefunden',
        };
        setAccessData(fallbackData);
      } else {
        console.error('Fehler beim Prüfen des Feature-Zugangs:', err);
        setError(err.message || 'Fehler beim Prüfen des Feature-Zugangs');
        setAccessData({
          hasAccess: false,
          featureStatus: 'beta',
          isBetaFeature: false,
          reason: 'Feature-Zugang konnte nicht geprüft werden',
        });
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bewusst userId/userRole
    // statt user (siehe Kommentar oben), user selbst wird im Closure gelesen
  }, [featureKey, isAuthenticated, userId, userRole, hasFcmsAccess, hasBitcoinAccess]);

  // Initial fetch und bei Änderungen
  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  // Cache invalidieren bei Logout
  useEffect(() => {
    if (!isAuthenticated) {
      invalidateFeatureAccessCache();
    }
  }, [isAuthenticated]);

  return {
    hasAccess: accessData?.hasAccess ?? false,
    isLoading,
    error,
    featureStatus: accessData?.featureStatus ?? null,
    isBetaFeature: accessData?.isBetaFeature ?? false,
    reason: accessData?.reason,
    refetch: fetchAccess,
  };
}

// ============================================
// Zusaetzliche Hooks
// ============================================

/**
 * Hook um zu pruefen ob der aktuelle User ein Beta-Tester ist
 */
export function useIsBetaTester(): { isBetaTester: boolean; isLoading: boolean } {
  const { user, isLoading } = useAuth();

  return {
    isBetaTester: user?.isBetaTester ?? false,
    isLoading,
  };
}

/**
 * Hook für mehrere Features gleichzeitig
 *
 * @param featureKeys - Array von Feature-Keys
 * @returns Map mit Feature-Key -> Zugangs-Info
 */
export function useMultipleFeatureAccess(featureKeys: string[]): Map<string, FeatureAccessResult> {
  const results = new Map<string, FeatureAccessResult>();

  for (const key of featureKeys) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const result = useFeatureAccess(key);
    results.set(key, result);
  }

  return results;
}

export default useFeatureAccess;
